/**
 * 004 — Render público v2 con el Design System (US1/US2).
 * Porta la composición de los templates de referencia del DS (BodaElegante.dc.html, etc.)
 * de forma **token-driven** (variables CSS), de modo que los 7 estilos adaptan
 * paleta/tipografía/ornamentos vía la clase `theme-*`. Anatomía fija (17 secciones),
 * gateada por interruptores 🎚; derivados ⚙️ y copy 🏛 inyectados; prellenados ✍️.
 */
import type { CSSProperties, ReactNode } from 'react';
import {
  CoverVeil, Ornament, Filigree, Monogram, Photo, DateBlock, Countdown,
  GuestPass, HonorList, EventCard, Itinerary, DressCode, PhotoGallery,
  NoteCard, GiftList, GiftCard, RsvpForm, Button,
} from '@/invitation/ds';
import { InvitationPayload } from '@/invitation/payload/types';
import { deriveFields } from '@/invitation/payload/derived';
import { orderedCouple } from '@/invitation/render/coupleOrder';
import { buildCalendarUrlFromPayload } from '@/invitation/render/calendarUrl';
import RsvpCerrado from '@/invitation/render/RsvpCerrado';
import { themeClass } from '@/invitation/payload/styles';
import {
  ROTULOS, NOTA_NINOS, NOTA_FOTOS, EVENTO_META, REGALO_TARJETAS,
  vestimenta as vestimentaCat,
} from '@/invitation/payload/system-catalog';
import {
  resolveHistoriaTexto, resolveHistoriaFrase, resolveRegalosMensaje,
  resolveEventoNota, resolveMomento, resolveVestimentaCopy,
} from '@/invitation/payload/resolve';
import BodaElegante from '@/invitation/templates/BodaElegante';
import { BodaTemplate } from '@/invitation/templates/BodaTemplate';
import { ESTILO_CONFIGS } from '@/invitation/templates/configs';

export interface InvitationV2Props {
  payload: InvitationPayload;
  open?: boolean;
  onEnter?: () => void;
  onRsvp?: (data: unknown) => void;
  rsvpSubmitted?: boolean;
  /** 008 · US1 — RSVP cerrado (deadline vencida): muestra aviso + contacto del coordinador. */
  rsvpClosed?: boolean;
  coordinador?: { url?: string; nombre?: string | null };
  /** 009 — Slot del álbum compartido (montado en la sección "Comparte tus fotos"). */
  album?: ReactNode;
}

const SECTION_PAD = 'clamp(56px, 8vw, 104px) clamp(24px, 6vw, 96px)';

/** Sección con ancho máximo, centrado y separación generosa (como el template DS). */
function Section({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <section style={{ position: 'relative', padding: SECTION_PAD, overflow: 'hidden', ...style }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gap: 'clamp(20px,2.4vw,32px)', justifyItems: 'center', textAlign: 'center' }}>
        {children}
      </div>
    </section>
  );
}

const GenericTemplate = ({ payload, open = true, onEnter, onRsvp, rsvpSubmitted, rsvpClosed, coordinador, album }: InvitationV2Props) => {
  const p = payload;
  const i = p.interruptores;
  const d = deriveFields(p.boda.fecha, p.novia.nombreCorto, p.novio.nombreCorto);
  const { primero: pNombre1, segundo: pNombre2 } = orderedCouple(p);
  const nombres = [pNombre1.nombreCorto, pNombre2.nombreCorto].filter(Boolean).join(' & ');
  const monthYear = d.valida ? `${d.mesNombre} ${d.anio}` : '';
  const calendarUrl = buildCalendarUrlFromPayload(p);

  // Fotos por sección: aún no hay editor de slots (US5), así que caen a la galería
  // para que hero y retrato no queden vacíos. La portada usa la 1ª; el retrato la 2ª.
  const galeria = p.fotos?.galeria ?? [];
  const portadaImg = p.fotos?.portada || galeria[0];
  const parejaImg = p.fotos?.pareja || galeria[1] || galeria[0];
  const hasRetratoFoto =
    i.retratos === 'pareja'
      ? Boolean(parejaImg)
      : i.retratos === 'individuales'
        ? Boolean(p.fotos?.novia || p.fotos?.novio)
        : false;
  const gifts = p.regalos ?? {};
  const hasGifts =
    (i.mesaRegalos && Boolean(gifts.tiendaUrl)) ||
    (i.transferencia && Boolean(gifts.banco || gifts.cuenta)) ||
    (i.transferenciaQR && Boolean(gifts.qrUrl)) ||
    i.efectivoSobre ||
    (i.listaRegalos && Boolean(gifts.sugerencias && gifts.sugerencias.length));

  const flourish: CSSProperties = {
    margin: 0,
    fontFamily: 'var(--font-flourish, var(--font-script))',
    color: 'var(--accent)',
    lineHeight: 1.1,
  };

  return (
    <div
      className={themeClass(p.estilo)}
      style={{ background: 'var(--surface)', color: 'var(--fg-primary)', fontFamily: 'var(--font-body, sans-serif)', overflowX: 'hidden' }}
    >
      {/* 1 · Sobre de entrada */}
      <CoverVeil
        names={nombres}
        eyebrow={ROTULOS.sobreEyebrow}
        date={d.fechaLarga}
        image={p.fotos?.portada}
        cta={ROTULOS.sobreCta}
        onEnter={onEnter}
        open={open}
      />

      {/* 2 · Portada — hero a sangre con foto de portada */}
      <section
        style={{
          position: 'relative',
          minHeight: '82svh',
          display: 'grid',
          alignItems: 'end',
          justifyItems: 'center',
          background: 'var(--contrast, #221913)',
          overflow: 'hidden',
          padding: 'clamp(64px,9vw,120px) clamp(24px,6vw,96px) clamp(72px,9vw,120px)',
        }}
      >
        {portadaImg && (
          <div style={{ position: 'absolute', inset: 0 }}>
            <Photo fill src={portadaImg} alt={nombres} />
          </div>
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(20,15,10,.45) 0%, transparent 30%, rgba(20,15,10,.30) 55%, rgba(20,15,10,.85) 100%)' }} />
        <div style={{ position: 'relative', textAlign: 'center', display: 'grid', gap: 'clamp(16px,2.2vw,28px)', justifyItems: 'center', maxWidth: 1000 }}>
          <p style={{ margin: 0, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'clamp(15px,1.5vw,20px)', color: 'var(--fg-on-contrast, #f4e9d4)', opacity: 0.82 }}>
            {ROTULOS.portadaFrase}
          </p>
          <h1 style={{ ...flourish, fontSize: 'clamp(48px,12vw,150px)', color: 'var(--fg-on-contrast, #f4e9d4)' }}>{nombres}</h1>
          <Ornament variant="flourish" width={280} tone="onDark" />
          {d.valida && (
            <p style={{ margin: 0, fontFamily: 'var(--font-caps)', fontSize: 'clamp(10px,.9vw,13px)', letterSpacing: '.35em', textTransform: 'uppercase', color: 'var(--fg-on-contrast, #f4e9d4)', opacity: 0.8 }}>
              {d.fechaNumerica}
            </p>
          )}
        </div>
      </section>

      {/* 3 · Nuestra historia */}
      <Section>
        <p style={{ ...flourish, fontSize: 'clamp(34px,4vw,54px)' }}>{ROTULOS.historia}</p>
        <Ornament variant="flourish" width={180} />
        <p style={{ margin: 0, maxWidth: 720, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'clamp(18px,1.9vw,27px)', lineHeight: 1.75, color: 'var(--fg-secondary)' }}>
          {resolveHistoriaTexto(p.historia?.texto)}
        </p>
      </Section>

      {/* 4 · Retratos (solo con foto) */}
      {i.retratos !== 'sin' && hasRetratoFoto && (
        <Section>
          {i.retratos === 'pareja' && parejaImg && (
            <div style={{ width: '100%', maxWidth: 340, boxShadow: '0 40px 70px -54px rgba(0,0,0,.6)' }}>
              <Photo src={parejaImg} shape="arch" ratio="2 / 3" alt={nombres} />
            </div>
          )}
          {i.retratos === 'individuales' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(16px,2.4vw,40px)', width: '100%', maxWidth: 620 }}>
              {p.fotos?.novia && <Photo src={p.fotos.novia} shape="arch" ratio="2 / 3" alt={p.novia.nombreCompleto || ''} />}
              {p.fotos?.novio && <Photo src={p.fotos.novio} shape="arch" ratio="2 / 3" alt={p.novio.nombreCompleto || ''} />}
            </div>
          )}
          {i.retratos === 'individuales' && (p.novia.nombreCompleto || p.novio.nombreCompleto) && (
            <p style={{ ...flourish, fontSize: 'clamp(28px,3vw,44px)' }}>
              {p.novia.nombreCompleto} <span style={{ opacity: 0.7 }}>&amp;</span> {p.novio.nombreCompleto}
            </p>
          )}
          <p style={{ margin: 0, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', color: 'var(--fg-secondary)' }}>
            {resolveHistoriaFrase(p.historia?.frase)}
          </p>
        </Section>
      )}

      {/* 5 · Reserva la fecha */}
      {d.valida && (
        <Section>
          <p style={{ ...flourish, fontSize: 'clamp(34px,4vw,54px)' }}>{ROTULOS.reservaFecha}</p>
          <Ornament variant="flourish" width={180} />
          <DateBlock weekday={d.diaSemana} day={d.dia} monthYear={monthYear} time={d.horaEnLetra} />
          {p.boda.fecha && (
            <div style={{ width: '100%', maxWidth: 440 }}>
              <Countdown target={p.boda.fecha} boxed />
            </div>
          )}
          {calendarUrl && (
            <Button variant="outline" size="md" onClick={() => window.open(calendarUrl, '_blank')}>
              {ROTULOS.reservaCta}
            </Button>
          )}
        </Section>
      )}

      {/* 6 · Pase del invitado (🔗) */}
      {p.invitado && (
        <Section>
          <div style={{ width: '100%', maxWidth: 560 }}>
            <GuestPass guest={p.invitado.nombre} passes={p.invitado.pases} />
          </div>
        </Section>
      )}

      {/* 7 · Con la bendición de */}
      {p.familia && p.familia.length > 0 && (
        <Section>
          <Filigree name="rama-botanica" width={340} style={{ position: 'absolute', top: '12%', right: -40, opacity: 0.06, pointerEvents: 'none' }} />
          <p style={{ ...flourish, fontSize: 'clamp(32px,3.6vw,48px)' }}>{ROTULOS.bendicion}</p>
          <Ornament variant="flourish" width={180} />
          <HonorList groups={p.familia.map((f) => ({ title: f.rol, names: [f.nombre] }))} columns={2} />
        </Section>
      )}

      {/* 8 · Eventos */}
      {p.eventos && p.eventos.length > 0 && (
        <Section>
          <p style={{ ...flourish, fontSize: 'clamp(34px,4vw,54px)' }}>{ROTULOS.eventos}</p>
          <Ornament variant="flourish" width={180} />
          <div style={{ display: 'grid', gap: 'clamp(18px,2.4vw,28px)', width: '100%', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {p.eventos.map((e, idx) => (
              <EventCard
                key={idx}
                icon={EVENTO_META[e.tipo].icon}
                title={EVENTO_META[e.tipo].titulo}
                time={e.hora}
                venue={e.sede}
                address={e.direccion}
                note={resolveEventoNota(e.tipo, e.nota)}
                actionLabel={ROTULOS.eventoAccion}
                onAction={e.mapaUrl ? () => window.open(e.mapaUrl, '_blank') : undefined}
              />
            ))}
          </div>
        </Section>
      )}

      {/* 9 · Itinerario */}
      {p.itinerario && p.itinerario.length > 0 && (
        <Section>
          <p style={{ ...flourish, fontSize: 'clamp(32px,3.6vw,48px)' }}>{ROTULOS.itinerario}</p>
          <Ornament variant="flourish" width={180} />
          <Itinerary
            steps={p.itinerario.map((it) => {
              const m = resolveMomento(it.momento, it.momentoLibre);
              return { icon: m.icon, time: it.hora, label: m.nombre, detail: it.detalle };
            })}
          />
        </Section>
      )}

      {/* 10 · Vestimenta */}
      {p.vestimenta?.codigo && (
        <Section>
          <p style={{ ...flourish, fontSize: 'clamp(32px,3.6vw,48px)' }}>{ROTULOS.vestimenta}</p>
          <Ornament variant="flourish" width={180} />
          <DressCode
            code={vestimentaCat(p.vestimenta.codigo)?.nombre ?? p.vestimenta.codigo}
            note={resolveVestimentaCopy(p.vestimenta.codigo)}
            palette={i.coloresSugeridos ? (p.vestimenta.paleta ?? []) : []}
            avoid={i.coloresReservados ? (p.vestimenta.reservados ?? []) : []}
          />
        </Section>
      )}

      {/* 11 · Galería */}
      {i.galeria && p.fotos?.galeria && p.fotos.galeria.length > 0 && (
        <Section>
          <p style={{ ...flourish, fontSize: 'clamp(32px,3.6vw,48px)' }}>{ROTULOS.galeria}</p>
          <Ornament variant="flourish" width={180} />
          <PhotoGallery items={p.fotos.galeria.map((src) => ({ src }))} columns={2} />
        </Section>
      )}

      {/* 12 · Notas para los invitados */}
      <Section>
        <div style={{ display: 'grid', gap: 'clamp(16px,2vw,26px)', width: '100%', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <NoteCard
            icon="baby"
            title={i.ninosPermitidos ? 'Los niños son bienvenidos' : 'Solo adultos'}
            body={i.ninosPermitidos ? NOTA_NINOS.permitidos : NOTA_NINOS.soloAdultos}
          />
          {i.fotosInvitados && (
            <NoteCard icon="camera" title="Comparte tus fotos" body={NOTA_FOTOS} hashtag={p.album?.hashtag} />
          )}
        </div>
        {i.fotosInvitados && album}
      </Section>

      {/* 13 · Mesa de regalos */}
      {i.regalos && hasGifts && (
        <Section>
          <p style={{ ...flourish, fontSize: 'clamp(32px,3.6vw,48px)' }}>{ROTULOS.regalos}</p>
          <Ornament variant="flourish" width={180} />
          <p style={{ margin: 0, maxWidth: 620, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 'clamp(17px,1.6vw,23px)', color: 'var(--fg-secondary)' }}>
            {resolveRegalosMensaje(gifts.mensaje)}
          </p>
          <div style={{ display: 'grid', gap: 'clamp(16px,2vw,26px)', width: '100%', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {i.mesaRegalos && gifts.tiendaUrl && (
              <GiftCard icon="gift" title={REGALO_TARJETAS.mesaRegalos} href={gifts.tiendaUrl} actionLabel="Abrir mesa" />
            )}
            {i.transferencia && (
              <GiftCard icon="credit-card" title={REGALO_TARJETAS.transferencia} description={gifts.banco} detail={gifts.cuenta} detailLabel={gifts.titular} actionLabel="Copiar número" />
            )}
            {i.transferenciaQR && gifts.qrUrl && (
              <GiftCard icon="credit-card" title={REGALO_TARJETAS.transferenciaQR} qr={gifts.qrUrl} />
            )}
            {i.efectivoSobre && <GiftCard icon="mail" title={REGALO_TARJETAS.efectivoSobre} />}
          </div>
          {i.listaRegalos && gifts.sugerencias && gifts.sugerencias.length > 0 && (
            <GiftList
              items={gifts.sugerencias.map((s) => ({ name: s.nombre, price: s.precio, description: s.descripcion }))}
              currency={gifts.moneda ?? 'Bs'}
            />
          )}
        </Section>
      )}

      {/* 14 · Confirmación (🔗) */}
      {p.invitado && (
        <Section>
          <p style={{ ...flourish, fontSize: 'clamp(32px,3.6vw,48px)' }}>{ROTULOS.confirmacion}</p>
          <Ornament variant="flourish" width={180} />
          <div style={{ width: '100%', maxWidth: 520 }}>
            {rsvpClosed ? (
              <RsvpCerrado coordinadorUrl={coordinador?.url} coordinadorNombre={coordinador?.nombre} />
            ) : (
              <RsvpForm
                guest={p.invitado.nombre}
                maxPasses={p.invitado.pases}
                deadline={p.rsvp?.fechaLimite}
                onSubmit={onRsvp}
                submitted={rsvpSubmitted}
              />
            )}
          </div>
        </Section>
      )}

      {/* 15 · Cierre */}
      <section style={{ padding: SECTION_PAD, background: 'var(--contrast, #221913)', color: 'var(--fg-on-contrast, #f4e9d4)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'grid', gap: 'clamp(16px,2vw,26px)', justifyItems: 'center', textAlign: 'center' }}>
          <Monogram left={(pNombre1.nombreCorto || '').charAt(0)} right={(pNombre2.nombreCorto || '').charAt(0)} tone="onDark" />
          <p style={{ ...flourish, fontSize: 'clamp(34px,5vw,64px)', color: 'var(--fg-on-contrast, #f4e9d4)' }}>{nombres}</p>
          <Ornament variant="flourish" width={220} tone="onDark" />
          {d.valida && (
            <p style={{ margin: 0, fontFamily: 'var(--font-display)', fontStyle: 'italic', opacity: 0.85 }}>
              Gracias por acompañarnos · {d.fechaLarga}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

/**
 * Dispatcher por estilo (004): cada estilo tiene su plantilla independiente y fiel a su
 * `.dc.html`. Los estilos aún no portados caen a `GenericTemplate` (token-driven).
 */
const cfgTemplate = (id: InvitationPayload['estilo']) =>
  (props: InvitationV2Props) => <BodaTemplate cfg={ESTILO_CONFIGS[id]} {...props} />;

// Elegante = archivo independiente (aprobado). Los otros 6 usan el motor compartido
// con su config; pueden "eyectarse" a un archivo propio si necesitan divergir.
const TEMPLATES: Partial<Record<InvitationPayload['estilo'], (props: InvitationV2Props) => JSX.Element>> = {
  elegante: BodaElegante,
  romantico: cfgTemplate('romantico'),
  epoca: cfgTemplate('epoca'),
  rustico: cfgTemplate('rustico'),
  costero: cfgTemplate('costero'),
  urbano: cfgTemplate('urbano'),
  moderno: cfgTemplate('moderno'),
};

const InvitationV2 = (props: InvitationV2Props) => {
  const Template = TEMPLATES[props.payload.estilo] ?? GenericTemplate;
  return <Template {...props} />;
};

export default InvitationV2;
